import { createRouter, createWebHistory } from 'vue-router/auto'
import { handleHotUpdate, routes } from 'vue-router/auto-routes'

import type { EnhancedRouteLocation } from './types'
import { useRouteCacheStore, useUserStore, useAppStore } from '@/stores'

import { isLogin } from '@/utils/auth'
import setPageTitle from '@/utils/set-page-title'
import { isPWAInstalled } from '@/utils/device'
import { syncUrlParamsToCookie } from '@/utils/pub-use'

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_APP_PUBLIC_PATH),
  routes,
  // 添加滚动行为配置
  scrollBehavior(to, from, savedPosition) {
    // 如果有保存的位置（浏览器前进后退），则恢复到该位置
    if (savedPosition) {
      return savedPosition
    }
    // 否则滚动到页面顶部
    return { top: 0, left: 0, behavior: 'smooth' }
  }
})

// This will update routes at runtime without reloading the page
if (import.meta.hot)
  handleHotUpdate(router)
// 白名单
const whiteList = ['/update']
router.beforeEach(async (to: EnhancedRouteLocation) => {
  // 统一处理 URL 参数存储到 cookie（pixel, channelId, channelName）
  // 如果 URL 中有这些参数，就存储到 cookie；如果 URL 中的参数和 cookie 中的不一样，就用 URL 中的最新值覆盖
  syncUrlParamsToCookie(['pixel', 'channelId', 'channelName'])

  // Route cache
  const routeCacheStore = useRouteCacheStore()
  const userStore = useUserStore()
  routeCacheStore.addRoute(to)

  // Set page title
  setPageTitle(to.meta.title)

  if (whiteList.includes(to.path)) {
    return
  }
  // 如果是404页面，跳过所有验证逻辑
  if (to.path === '/404') {
    return true
  }

  // 检查是否需要登录验证
  const requiresAuth = to.meta.requiresAuth !== false // 默认需要验证

  if (requiresAuth && isLogin() && !userStore.userInfo?.id) {
    try {
      await userStore.info()
    } catch (error) {
      // 如果获取用户信息失败，清除登录状态并跳转到首页
      console.error('获取用户信息失败:', error)
      userStore.clearUserInfo()
      // 只清除路由缓存，不影响其他用户信息
      routeCacheStore.clearRouteCache()
      return '/'
    }
  }
  // 判断域名是否需要跳转，必须是推广域名，如果是跳转到注册页，需要拉起注册弹框
  // 仅仅在非pwa环境和apk环境需要
  const appStore = useAppStore()
  if (appStore.domainInfo.domainType == null && appStore.domainInfo.domainRedirect == null) {
    try {
      await appStore.getDomainInfo()
    } catch (e) {
      // 获取失败则不做域名跳转限制
    }
  }

  // 如果是跳转到404页面，不执行域名跳转逻辑
  if (to.path !== '/404' && !isPWAInstalled() && !window.platformGame) {
    const redirectFlag = Number(appStore.domainInfo.domainRedirect)
    const type = Number(appStore.domainInfo.domainType)
    if (type === 1 && !Number.isNaN(redirectFlag)) {
      const desiredPath = redirectFlag === 0 ? '/download' : (redirectFlag === 2 ? '/?autoRegister=true' : '/')
      if (to.path !== desiredPath) {
        return desiredPath
      }
    }
  }
})

router.afterEach(() => {
})

export default router
